const hasValue = (v) => (v || v === 0) && v !== '';
const n = (v, fallback = 0) => {
  const num = Number(v);
  return Number.isFinite(num) ? num : fallback;
};
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));

export const isStemCompletelyOnBase = ({ B, L, ex, ey, wx, wy }) => {
  const b = Number(B);
  const l = Number(L);
  const exv = Number(ex);
  const eyv = Number(ey);
  const wxv = Number(wx);
  const wyv = Number(wy);

  if (![b, l, exv, eyv, wxv, wyv].every((v) => Number.isFinite(v))) return false;
  if (b <= 0 || l <= 0 || wxv <= 0 || wyv <= 0) return false;

  return Math.abs(exv) + wxv / 2 <= b / 2 && Math.abs(eyv) + wyv / 2 <= l / 2;
};

export default function buildHansenSketchModel(input = {}) {
  const raw = { ...input };

  const frame = {
    VW: 900,
    VH: 420,
    yGround: 48,
    yBottom: 420 - 16,
    xInset: 24,
  };
  // Render guard keeps moving stem/column away from UI overlays and outer frame edges.
  const xRenderGuard = {
    leftReserve: 36,
    rightReserve: 210,
    edgeGap: 2,
  };
  frame.frameStyle = { width: '100%', maxWidth: 1200, minWidth: 0, aspectRatio: `${frame.VW} / ${frame.VH}` };

  const drawB = Math.max(0.8, n(raw.fndWidth, 2.5));
  const drawL = Math.max(0.8, n(raw.fndLength, 3));
  const footingScalePxPerM = 52;
  const footing = {
    width: clamp(drawB * footingScalePxPerM, 160, 520),
    height: 40,
    top: 164,
  };
  footing.cx = frame.VW / 2;
  footing.left = footing.cx - footing.width / 2;
  footing.cy = footing.top + footing.height / 2;
  footing.right = footing.left + footing.width;
  footing.bottom = footing.top + footing.height;

  // Shared normalized geometry so front/top stay consistent.
  const geometry = {
    B: drawB,
    L: drawL,
    exRaw: n(raw.eccentricityX, 0),
    eyRaw: n(raw.eccentricityY, 0),
    wxRaw: hasValue(raw.columnDimensionX) ? Math.max(0.2, n(raw.columnDimensionX)) : 0.6,
    wyRaw: hasValue(raw.columnDimensionY) ? Math.max(0.2, n(raw.columnDimensionY)) : 0.6,
  };
  const stemCompletelyOnBase = isStemCompletelyOnBase({
    B: geometry.B,
    L: geometry.L,
    ex: geometry.exRaw,
    ey: geometry.eyRaw,
    wx: geometry.wxRaw,
    wy: geometry.wyRaw,
  });

  const topCy = frame.yGround + (frame.yBottom - frame.yGround) / 2;
  geometry.LRender = geometry.L;
  geometry.exRatio = geometry.exRaw / Math.max(1e-6, geometry.B);
  geometry.eyRatio = geometry.eyRaw / Math.max(1e-6, geometry.L);
  geometry.wxRatio = Math.max(0, geometry.wxRaw / Math.max(1e-6, geometry.B));
  geometry.wyRatio = Math.max(0, geometry.wyRaw / Math.max(1e-6, geometry.L));

  const front = {};
  front.B = geometry.B;
  front.wxRaw = geometry.wxRaw;
  front.stemWRaw = Math.max(18, geometry.wxRatio * footing.width);
  front.exMax = Math.max(0.2, front.B * 0.4);
  front.ex = geometry.exRatio * front.B;
  front.exPx = geometry.exRatio * footing.width;
  front.stemCxRaw = footing.cx + front.exPx;
  const frontStemMinCx = frame.xInset + xRenderGuard.leftReserve + xRenderGuard.edgeGap;
  const frontStemMaxCx = frame.VW - frame.xInset - xRenderGuard.rightReserve - xRenderGuard.edgeGap;
  const canClampFrontX = frontStemMinCx <= frontStemMaxCx;
  front.stemCx = canClampFrontX ? clamp(front.stemCxRaw, frontStemMinCx, frontStemMaxCx) : footing.cx;
  front.exDisplayClipped = Math.abs(front.stemCx - front.stemCxRaw) > 0.5;
  const frontHalfAvail = Math.max(9, Math.min(front.stemCx - frontStemMinCx, frontStemMaxCx - front.stemCx));
  const frontStemWMaxSym = Math.max(18, frontHalfAvail * 2);
  front.stemW = Math.min(front.stemWRaw, frontStemWMaxSym);
  front.wxDisplayClipped = Math.abs(front.stemW - front.stemWRaw) > 0.5;
  front.stemWEmpty = Math.max(18, Math.min(front.stemW, footing.width * 0.2));
  front.isStemDisplayClipped = front.exDisplayClipped || front.wxDisplayClipped;
  front.displayClippedTokens = [];
  if (front.exDisplayClipped) front.displayClippedTokens.push('ex');
  if (front.wxDisplayClipped) front.displayClippedTokens.push('wx');
  front.yStemTop = frame.yGround;
  front.yFootTop = footing.top;
  front.yFootBottom = footing.bottom;
  front.stemH = front.yFootTop - front.yStemTop;
  front.yLoadTop = front.yStemTop - 34;

  const Df = hasValue(raw.df) ? Math.max(0, n(raw.df)) : 0;
  const GWTin = hasValue(raw.gwt) ? Math.max(0, n(raw.gwt)) : null;
  const soilPx = frame.yBottom - frame.yGround;
  const soilDepthM = Df > 0 ? (Df * soilPx) / Math.max(1, front.yFootBottom - frame.yGround) : 6;
  const depthToY = (depthM) => frame.yGround + (depthM / soilDepthM) * soilPx;

  front.hasGwt = GWTin !== null;
  front.yGwt = front.hasGwt ? clamp(depthToY(GWTin), frame.yGround, frame.yBottom) : null;
  front.gwtClampedBottom = front.hasGwt ? GWTin > soilDepthM : false;

  front.betaNum = clamp(n(raw.beta, 0), 0, 60);
  front.nuNum = clamp(n(raw.nu, 0), 0, 30);
  const betaRad = (front.betaNum * Math.PI) / 180;
  const nuRad = (front.nuNum * Math.PI) / 180;
  front.slopeSpan = 220;
  front.slopeRise = clamp(Math.tan(betaRad) * front.slopeSpan * 0.12, 0, 28);
  front.baseTiltSpan = 110;
  front.baseTiltRise = clamp(Math.tan(nuRad) * front.baseTiltSpan * 0.16, 0, 20);

  front.showDf = hasValue(raw.df);
  front.showDw = hasValue(raw.df) && hasValue(raw.gwt) && n(raw.gwt) > n(raw.df);
  front.dwValue = front.showDw ? n(raw.gwt) - n(raw.df) : null;
  front.showB = hasValue(raw.fndWidth);
  front.showEx = Math.abs(front.exPx) > 0.5;
  front.isStemCompletelyOnBase = stemCompletelyOnBase;
  front.showV = hasValue(raw.normal_v);
  front.showHb = hasValue(raw.hb);
  front.showHl = hasValue(raw.hl);
  front.showBeta = front.betaNum > 0;
  front.showNu = front.nuNum > 0;
  front.noInputs =
    !hasValue(raw.fndWidth) &&
    !hasValue(raw.fndLength) &&
    !hasValue(raw.df) &&
    !hasValue(raw.gwt) &&
    !hasValue(raw.cohesion) &&
    !hasValue(raw.phi) &&
    !hasValue(raw.gamma) &&
    !hasValue(raw.hb) &&
    !hasValue(raw.hl) &&
    !hasValue(raw.normal_v) &&
    !hasValue(raw.beta) &&
    !hasValue(raw.nu) &&
    !hasValue(raw.eccentricityX) &&
    !hasValue(raw.eccentricityY) &&
    !hasValue(raw.columnDimensionX) &&
    !hasValue(raw.columnDimensionY) &&
    !hasValue(raw.caCoefficient) &&
    !hasValue(raw.alpha1) &&
    !hasValue(raw.alpha2) &&
    !hasValue(raw.bearingCapacityFactorOfSafety) &&
    !hasValue(raw.slidingFactorOfSafety);

  const top = {};
  top.B = geometry.B;
  top.L = geometry.L;
  top.footingW = footing.width;
  top.LCap = geometry.L;
  top.LRender = geometry.LRender;
  top.footingHRaw = Math.max(24, (top.footingW * top.LRender) / Math.max(1e-6, top.B));
  const topVerticalClearance = 40;
  const topMaxDisplayH = Math.max(24, frame.yBottom - frame.yGround - topVerticalClearance * 2);
  top.footingH = Math.min(top.footingHRaw, topMaxDisplayH);
  top.cx = frame.xInset + (frame.VW - frame.xInset * 2) / 2;
  top.cy = topCy;
  top.left = top.cx - top.footingW / 2;
  top.right = top.cx + top.footingW / 2;
  top.top = top.cy - top.footingH / 2;
  top.bottom = top.cy + top.footingH / 2;
  const topRawTop = top.cy - top.footingHRaw / 2;
  const topRawBottom = top.cy + top.footingHRaw / 2;
  top.clipTop = topRawTop < frame.yGround;
  top.clipBottom = topRawBottom > frame.yBottom;
  top.isLengthClipped = top.clipTop || top.clipBottom;
  top.exMax = Math.max(0.2, top.B * 0.4);
  top.eyMax = Math.max(0.2, top.LRender * 0.4);
  top.ex = geometry.exRatio * top.B;
  top.ey = geometry.eyRatio * top.LRender;
  top.exPx = geometry.exRatio * top.footingW;
  top.eyPx = geometry.eyRatio * top.footingH;

  top.wxRaw = geometry.wxRaw;
  top.wyRaw = geometry.wyRaw;
  top.colWRaw = Math.max(8, geometry.wxRatio * top.footingW);
  top.colHRaw = Math.max(8, geometry.wyRatio * top.footingH);
  top.colH = top.colHRaw;
  top.colW = top.colWRaw;
  top.colWEmpty = Math.max(8, Math.min(top.colW, top.footingW * 0.2));

  top.colCxRaw = top.cx + top.exPx;
  top.colCyRaw = top.cy - top.eyPx;
  const topColMinCx = frame.xInset + xRenderGuard.leftReserve + xRenderGuard.edgeGap;
  const topColMaxCx = frame.VW - frame.xInset - xRenderGuard.rightReserve - xRenderGuard.edgeGap;
  const topColMinCy = frame.yGround + 2;
  const topColMaxCy = frame.yBottom - 2;
  const canClampCx = topColMinCx <= topColMaxCx;
  const canClampCy = topColMinCy <= topColMaxCy;
  top.colCx = canClampCx ? clamp(top.colCxRaw, topColMinCx, topColMaxCx) : top.cx;
  top.colCy = canClampCy ? clamp(top.colCyRaw, topColMinCy, topColMaxCy) : top.cy;
  const topHalfAvailX = Math.max(4, Math.min(top.colCx - topColMinCx, topColMaxCx - top.colCx));
  const topColWMaxSym = Math.max(8, topHalfAvailX * 2);
  top.colW = Math.min(top.colWRaw, topColWMaxSym);
  const topHalfAvailY = Math.max(4, Math.min(top.colCy - topColMinCy, topColMaxCy - top.colCy));
  const topColHMaxSym = Math.max(8, topHalfAvailY * 2);
  top.colH = Math.min(top.colHRaw, topColHMaxSym);
  top.colWEmpty = Math.max(8, Math.min(top.colW, top.footingW * 0.2));
  top.wxDisplayClipped = Math.abs(top.colW - top.colWRaw) > 0.5;
  top.wyDisplayClipped = Math.abs(top.colH - top.colHRaw) > 0.5;
  top.exDisplayClipped = Math.abs(top.colCx - top.colCxRaw) > 0.5;
  top.eyDisplayClipped = Math.abs(top.colCy - top.colCyRaw) > 0.5;
  top.isStemDisplayClipped = top.exDisplayClipped || top.eyDisplayClipped || top.wxDisplayClipped || top.wyDisplayClipped;
  top.displayClippedTokens = [];
  if (top.exDisplayClipped) top.displayClippedTokens.push('ex');
  if (top.eyDisplayClipped) top.displayClippedTokens.push('ey');
  if (top.wxDisplayClipped) top.displayClippedTokens.push('wx');
  if (top.wyDisplayClipped) top.displayClippedTokens.push('wy');

  top.showEx = Math.abs(top.exPx) > 0.5;
  top.showEy = Math.abs(top.eyPx) > 0.5;
  top.isStemCompletelyOnBase = stemCompletelyOnBase;
  top.showHb = hasValue(raw.hb);
  top.showHl = hasValue(raw.hl);
  top.noInputs =
    !hasValue(raw.fndWidth) &&
    !hasValue(raw.fndLength) &&
    !hasValue(raw.eccentricityX) &&
    !hasValue(raw.eccentricityY) &&
    !hasValue(raw.columnDimensionX) &&
    !hasValue(raw.columnDimensionY) &&
    !hasValue(raw.hb) &&
    !hasValue(raw.hl) &&
    !hasValue(raw.normal_v);

  return { raw, frame, footing, front, top };
}
